import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import PaginationComponent from "../../Components/PaginationControls/PaginationControls";
import useAxiosHelper from "../../Common/AxiosHelper";
import { ApiPaths } from "../../Config/ApiPath";
import { BasicInfo } from "../../Config/BasicInfo";
import moment from "moment";
import coinLogo from "../../Images/coin.png";
import {
  ArrowDownLeft,
  ArrowUpRight,
  SlidersHorizontal,
  X,
  RotateCcw,
  Search,
  Inbox,
  Calendar,
  Receipt,
  Copy,
  Check,
} from "lucide-react";
import "./TransactionHistoryApp.css";

const TOKEN_SOURCES = ["token_income", "decent_token", "token_withdrawal"];
const isTokenTransaction = (tx) =>
  tx?.wallet_type === "token_wallet" || TOKEN_SOURCES.includes(tx?.source);

const EMPTY_SEARCH = {
  startDate: "",
  endDate: "",
  debit_credit: "",
  source: [],
};

// Friendlier names for non-income sources (income names come from the wallets)
const SOURCE_LABELS = {
  topup: "Package Purchase",
  deposit: "Deposit",
  fund_request: "Fund Deposit",
  withdrawal: "Withdrawal",
  fund_transfer: "Fund Transfer",
  main_to_fund_transfer: "Main → Fund Transfer",
  principal_withdrawal: "Principal Withdrawal",
};

const titleCase = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const PaymentTransactionHistory = () => {
  const { AxiosGet } = useAxiosHelper();
  const [loading, setLoading] = useState(false);
  const [paymentTransaction, setPaymentTransaction] = useState(null); // null = first load
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [limitFilter, setLimitFilter] = useState(10);
  const [filterVisibility, setFilterVisibility] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  const [debitSum, setDebitSum] = useState(0);
  const [creditSum, setCreditSum] = useState(0);
  const [tokenCreditSum, setTokenCreditSum] = useState(0);
  const [tokenDebitSum, setTokenDebitSum] = useState(0);

  const [search, setSearch] = useState(EMPTY_SEARCH);
  // date range is edited in the sheet and only applied on "Show results"
  const [draftDates, setDraftDates] = useState({ startDate: "", endDate: "" });

  const [sourceData, setSourceData] = useState([]); // /get_all_source (if available)
  const [walletNames, setWalletNames] = useState({}); // slug -> wallet name
  const [selected, setSelected] = useState(null); // transaction shown in the detail sheet
  const [copied, setCopied] = useState(false);
  const chipsRef = useRef(null);

  const toastFailed = (msg) => toast.error(msg);
  const cur = companyData?.currency_sign || "$";

  useEffect(() => {
    CompanyInfo();
    getSource();
    getWalletNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function CompanyInfo() {
    try {
      const data = localStorage.getItem("companyData");
      if (data) setCompanyData(JSON.parse(data));
    } catch (error) {
      BasicInfo.isDebug && console.error("CompanyInfo error:", error);
    }
  }

  const getSource = async () => {
    try {
      const res = await AxiosGet(ApiPaths.get_all_source);
      setSourceData(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      BasicInfo.isDebug && console.error("Error fetching source data:", e?.message);
    }
  };

  // Income wallet names double as readable source names (direct_income -> "Referral Income")
  const getWalletNames = async () => {
    try {
      const res = await AxiosGet(ApiPaths.getWallets);
      const map = {};
      (res?.wallets || []).forEach((w) => {
        if (w?.slug) map[w.slug] = w.name;
      });
      setWalletNames(map);
    } catch (e) {
      BasicInfo.isDebug && console.error(e);
    }
  };

  const sourceLabel = (slug) => walletNames[slug] || SOURCE_LABELS[slug] || titleCase(slug);

  // Source chips: grouped /get_all_source data when the API provides it,
  // otherwise the income wallets plus package purchases.
  const sourceOptions = useMemo(() => {
    if (sourceData.length) {
      const map = new Map();
      sourceData.forEach((s) => {
        const key = `${s.id}-${s.name}`;
        if (!map.has(key)) map.set(key, { key, name: s.name, values: [] });
        map.get(key).values.push(s.value);
      });
      return Array.from(map.values());
    }
    const incomeSlugs = Object.keys(walletNames).filter((slug) => /income/.test(slug));
    return [
      { key: "topup", name: SOURCE_LABELS.topup, values: ["topup"] },
      ...incomeSlugs.map((slug) => ({ key: slug, name: walletNames[slug], values: [slug] })),
    ];
  }, [sourceData, walletNames]);

  useEffect(() => {
    FetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limitFilter]);

  const FetchData = async (page = currentPage, limit = limitFilter, currentSearch = search) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      Object.entries(currentSearch).forEach(([key, value]) => {
        if (key === "source") {
          (value || []).forEach((src) => queryParams.append("source", src));
        } else if (value !== "") {
          queryParams.append(key, value);
        }
      });

      const response = await AxiosGet(`${ApiPaths.getPaymentTransaction}?${queryParams.toString()}`);
      BasicInfo.isDebug && console.log("Payment Transaction History:", response);

      setPaymentTransaction(response?.data || []);
      setTotalPages(response?.totalPages || 1);
      setDebitSum(response?.debitSum || 0);
      setCreditSum(response?.creditSum || 0);
      setTokenCreditSum(response?.tokenCreditSum || 0);
      setTokenDebitSum(response?.tokenDebitSum || 0);
    } catch (error) {
      console.error("Error fetching payment transactions:", error);
      toastFailed("Failed to fetch payment transactions.");
      setPaymentTransaction([]);
      setTotalPages(1);
      setDebitSum(0);
      setCreditSum(0);
      setTokenCreditSum(0);
      setTokenDebitSum(0);
    } finally {
      setLoading(false);
    }
  };

  // apply a filter change immediately (type / source chips)
  const applySearch = (next) => {
    setSearch(next);
    setCurrentPage(1);
    FetchData(1, limitFilter, next);
  };

  const setType = (debit_credit) => applySearch({ ...search, debit_credit });

  // one source at a time — the API filters `source` by exact match
  const toggleSource = (opt) => {
    const isOn = opt.values.length === search.source.length && opt.values.every((v) => search.source.includes(v));
    applySearch({ ...search, source: isOn ? [] : opt.values });
  };

  const openFilters = () => {
    setDraftDates({ startDate: search.startDate, endDate: search.endDate });
    setFilterVisibility(true);
  };

  const handleSearch = () => {
    applySearch({ ...search, ...draftDates });
    setFilterVisibility(false);
  };

  const resetData = () => {
    setSearch(EMPTY_SEARCH);
    setDraftDates({ startDate: "", endDate: "" });
    setLimitFilter(10);
    setCurrentPage(1);
    setFilterVisibility(false);
    FetchData(1, 10, EMPTY_SEARCH);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // sheets: Escape closes, page behind stays put
  const sheetOpen = filterVisibility || !!selected;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setFilterVisibility(false);
        setSelected(null);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen]);

  /* ---------- formatting ---------- */
  const money = (v) =>
    `${cur}${(parseFloat(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
  const tokens = (v) => (parseFloat(v) || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });
  const amountOf = (x) => (isTokenTransaction(x) ? tokens(x.amount) : money(x.amount));

  // debit rows use `status`, credit rows use `release` — same rule as before
  const statusOf = (x) => {
    if (x?.debit_credit === "debit") {
      return x.status === 0
        ? { key: "wait", label: "Pending" }
        : x.status === 1
        ? { key: "ok", label: "Success" }
        : { key: "done", label: "Completed" };
    }
    return x?.release === 0
      ? { key: "wait", label: "Pending Release" }
      : x?.release === 1
      ? { key: "ok", label: "Released" }
      : { key: "done", label: "Completed" };
  };

  // group rows by day, app-style
  const groups = useMemo(() => {
    const out = [];
    (paymentTransaction || []).forEach((x, i) => {
      const m = x?.time ? moment(x.time) : null;
      const key = m ? m.format("YYYY-MM-DD") : "unknown";
      const label = !m
        ? "Unknown date"
        : m.isSame(moment(), "day")
        ? "Today"
        : m.isSame(moment().subtract(1, "day"), "day")
        ? "Yesterday"
        : m.format("ddd, DD MMM YYYY");
      let g = out[out.length - 1];
      if (!g || g.key !== key) {
        g = { key, label, items: [] };
        out.push(g);
      }
      g.items.push({ x, index: i });
    });
    return out;
  }, [paymentTransaction]);

  const credit = parseFloat(creditSum) || 0;
  const debit = parseFloat(debitSum) || 0;
  const net = credit - debit;
  const creditShare = credit + debit > 0 ? (credit / (credit + debit)) * 100 : 50;
  const hasTokenTotals = (parseFloat(tokenCreditSum) || 0) > 0 || (parseFloat(tokenDebitSum) || 0) > 0;
  const datesOn = Boolean(search.startDate || search.endDate);
  const anyFilter = datesOn || search.debit_credit || search.source.length > 0 || Number(limitFilter) !== 10;
  const list = paymentTransaction || [];
  const firstLoad = paymentTransaction === null;

  const copyId = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      BasicInfo.isDebug && console.log(e);
    }
  };

  return (
    <section className="thPage">
      {/* ---------- Summary ---------- */}
      <div className="thHero">
        <div className="thHeroTop">
          <span className="thEyebrow">
            <Receipt size={13} /> Transactions
          </span>
          {datesOn && (
            <span className="thRange">
              <Calendar size={12} />
              {search.startDate ? moment(search.startDate).format("DD MMM") : "Start"} –{" "}
              {search.endDate ? moment(search.endDate).format("DD MMM") : "Today"}
            </span>
          )}
        </div>
        <div className="thNetLabel">Net flow{anyFilter ? " (filtered)" : ""}</div>
        <div className={`thNet ${net < 0 ? "neg" : ""}`}>
          {net < 0 ? "−" : "+"}
          {money(Math.abs(net))}
        </div>

        <div className="thFlow">
          <span className="thFlowBar">
            <span style={{ width: `${creditShare}%` }} />
          </span>
          <div className="thFlowCols">
            <div>
              <span className="thFlowIcon in">
                <ArrowDownLeft size={16} />
              </span>
              <span>
                <small>Total credit</small>
                <b>{money(credit)}</b>
              </span>
            </div>
            <div>
              <span className="thFlowIcon out">
                <ArrowUpRight size={16} />
              </span>
              <span>
                <small>Total debit</small>
                <b>{money(debit)}</b>
              </span>
            </div>
          </div>
        </div>

        {hasTokenTotals && (
          <div className="thTokenRow">
            <img src={coinLogo} alt="" />
            Tokens: +{tokens(tokenCreditSum)} / −{tokens(tokenDebitSum)}
          </div>
        )}
      </div>

      {/* ---------- Filters ---------- */}
      <div className="thBar">
        <div className="thSeg" role="group" aria-label="Transaction type">
          {[
            { label: "All", value: "" },
            { label: "Credit", value: "credit" },
            { label: "Debit", value: "debit" },
          ].map((t) => (
            <button
              key={t.label}
              type="button"
              className={search.debit_credit === t.value ? "isOn" : ""}
              onClick={() => setType(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={`thFilterBtn ${datesOn || Number(limitFilter) !== 10 ? "isOn" : ""}`}
          onClick={openFilters}
          aria-label="Date range and page size"
        >
          <SlidersHorizontal size={18} />
          {(datesOn || Number(limitFilter) !== 10) && <i />}
        </button>
      </div>

      {sourceOptions.length > 0 && (
        <div className="thChips" ref={chipsRef} role="group" aria-label="Source">
          <button
            type="button"
            className={`thChip ${search.source.length === 0 ? "isOn" : ""}`}
            onClick={() => applySearch({ ...search, source: [] })}
          >
            All sources
          </button>
          {sourceOptions.map((opt) => {
            const on = opt.values.length === search.source.length && opt.values.every((v) => search.source.includes(v));
            return (
              <button key={opt.key} type="button" className={`thChip ${on ? "isOn" : ""}`} onClick={() => toggleSource(opt)}>
                {opt.name}
              </button>
            );
          })}
        </div>
      )}

      {/* ---------- List ---------- */}
      {firstLoad || (loading && !list.length) ? (
        <div className="thGroup">
          <span className="thGroupHead thSkelLine" style={{ width: 90 }} />
          <div className="thCard">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="thRow thSkel">
                <span className="dot" />
                <span className="lines">
                  <span />
                  <span />
                </span>
                <span className="amt" />
              </div>
            ))}
          </div>
        </div>
      ) : list.length === 0 ? (
        <div className="thEmpty">
          <span>
            <Inbox size={26} />
          </span>
          <b>No transactions found</b>
          <small>{anyFilter ? "Nothing matches these filters." : "Your deposits, purchases and earnings will appear here."}</small>
          {anyFilter && (
            <button type="button" onClick={resetData}>
              <RotateCcw size={14} /> Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className={`thGroups ${loading ? "isBusy" : ""}`}>
          {groups.map((g) => (
            <div className="thGroup" key={g.key}>
              <div className="thGroupHead">{g.label}</div>
              <div className="thCard">
                {g.items.map(({ x, index }) => {
                  const isCredit = x?.debit_credit !== "debit";
                  const st = statusOf(x);
                  return (
                    <button
                      key={x?._id || x?.id || index}
                      type="button"
                      className="thRow"
                      onClick={() => setSelected({ x, index })}
                    >
                      <span className={`thIcon ${isCredit ? "in" : "out"} ${isTokenTransaction(x) ? "token" : ""}`}>
                        {isTokenTransaction(x) ? (
                          <img src={coinLogo} alt="" />
                        ) : isCredit ? (
                          <ArrowDownLeft size={18} />
                        ) : (
                          <ArrowUpRight size={18} />
                        )}
                      </span>
                      <span className="thRowText">
                        <b>{sourceLabel(x?.source)}</b>
                        <small>
                          {x?.time ? moment(x.time).format("hh:mm A") : "—"}
                          {x?.from ? ` · ${x.from}` : ""}
                        </small>
                      </span>
                      <span className="thRowRight">
                        <b className={isCredit ? "in" : "out"}>
                          {isCredit ? "+" : "−"}
                          {amountOf(x)}
                        </b>
                        <span className={`thStatus ${st.key}`}>{st.label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <div className="thPager">
          <PaginationComponent currentPage={currentPage} totalPages={totalPages} onPageChange={handlePagination} />
        </div>
      )}

      {/* ---------- Filter sheet ---------- */}
      {filterVisibility && (
        <div className="thOverlay" onClick={() => setFilterVisibility(false)}>
          <div className="thSheet" role="dialog" aria-modal="true" aria-labelledby="thFilterTitle" onClick={(e) => e.stopPropagation()}>
            <span className="thHandle" aria-hidden="true" />
            <button type="button" className="thClose" onClick={() => setFilterVisibility(false)} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="thFilterTitle">Filter transactions</h3>

            <div className="thQuick">
              {[
                { label: "Today", from: moment(), to: moment() },
                { label: "7 days", from: moment().subtract(6, "days"), to: moment() },
                { label: "30 days", from: moment().subtract(29, "days"), to: moment() },
                { label: "This month", from: moment().startOf("month"), to: moment() },
              ].map((q) => {
                const s = q.from.format("YYYY-MM-DD");
                const e = q.to.format("YYYY-MM-DD");
                const on = draftDates.startDate === s && draftDates.endDate === e;
                return (
                  <button
                    key={q.label}
                    type="button"
                    className={`thChip ${on ? "isOn" : ""}`}
                    onClick={() => setDraftDates({ startDate: s, endDate: e })}
                  >
                    {q.label}
                  </button>
                );
              })}
            </div>

            <div className="thFieldRow">
              <label className="thField">
                <span>From date</span>
                <input
                  type="date"
                  value={draftDates.startDate}
                  onChange={(e) => setDraftDates((d) => ({ ...d, startDate: e.target.value }))}
                />
              </label>
              <label className="thField">
                <span>To date</span>
                <input
                  type="date"
                  value={draftDates.endDate}
                  onChange={(e) => setDraftDates((d) => ({ ...d, endDate: e.target.value }))}
                />
              </label>
            </div>

            <div className="thField">
              <span>Per page</span>
              <div className="thSeg wide">
                {[10, 20, 50, 100].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={Number(limitFilter) === n ? "isOn" : ""}
                    onClick={() => {
                      setLimitFilter(n);
                      setCurrentPage(1);
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="thSheetActions">
              <button type="button" className="thBtn ghost" onClick={resetData}>
                <RotateCcw size={16} /> Reset all
              </button>
              <button type="button" className="thBtn primary" onClick={handleSearch}>
                <Search size={16} /> Show results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Detail sheet ---------- */}
      {selected && (() => {
        const { x, index } = selected;
        const isCredit = x?.debit_credit !== "debit";
        const st = statusOf(x);
        const txId = x?.tx_Id ?? x?.order_Id;
        return (
          <div className="thOverlay" onClick={() => setSelected(null)}>
            <div className="thSheet" role="dialog" aria-modal="true" aria-labelledby="thDetailTitle" onClick={(e) => e.stopPropagation()}>
              <span className="thHandle" aria-hidden="true" />
              <button type="button" className="thClose" onClick={() => setSelected(null)} aria-label="Close">
                <X size={18} />
              </button>
              <div className="thDetailHead">
                <span className={`thIcon lg ${isCredit ? "in" : "out"}`}>
                  {isCredit ? <ArrowDownLeft size={24} /> : <ArrowUpRight size={24} />}
                </span>
                <h3 id="thDetailTitle">{sourceLabel(x?.source)}</h3>
                <b className={`thDetailAmt ${isCredit ? "in" : "out"}`}>
                  {isCredit ? "+" : "−"}
                  {amountOf(x)}
                </b>
                <span className={`thStatus ${st.key}`}>{st.label}</span>
              </div>
              <dl className="thDetailList">
                <div>
                  <dt>Type</dt>
                  <dd>{x?.debit_credit ? titleCase(x.debit_credit) : "N/A"}</dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>{titleCase(x?.source)}</dd>
                </div>
                {x?.wallet_type && (
                  <div>
                    <dt>Wallet</dt>
                    <dd>{walletNames[x.wallet_type] || titleCase(x.wallet_type)}</dd>
                  </div>
                )}
                {(x?.from || x?.fromname) && (
                  <div>
                    <dt>From</dt>
                    <dd>
                      {x?.fromname ? `${x.fromname} ` : ""}
                      {x?.from ? `(${x.from})` : ""}
                    </dd>
                  </div>
                )}
                <div>
                  <dt>Date & time</dt>
                  <dd>{x?.time ? moment(x.time).format("DD MMM YYYY, hh:mm A") : "N/A"}</dd>
                </div>
                {txId !== undefined && txId !== null && (
                  <div>
                    <dt>Transaction ID</dt>
                    <dd>
                      <button type="button" className="thCopy" onClick={() => copyId(txId)}>
                        #{txId} {copied ? <Check size={13} /> : <Copy size={13} />}
                      </button>
                    </dd>
                  </div>
                )}
                <div>
                  <dt>Row</dt>
                  <dd>#{(currentPage - 1) * limitFilter + (index + 1)}</dd>
                </div>
              </dl>
            </div>
          </div>
        );
      })()}
    </section>
  );
};

export default PaymentTransactionHistory;
